import { styled, css } from '@vebox/etui-system';
import { Component, createRef, createElement } from 'react';
import { c as _defineProperty, a as _objectSpread2, b as _extends } from '../_rollupPluginBabelHelpers-3b7bd988.js';
import { Div } from '@vebox/etui-system-elements';
import { defaultProps } from './defaultSettings.js';
import { translateIsValid, getClientRect, testPassiveEventSupport, notUndefOrNull } from './utils/index.js';
import InnerWrapper from './InnerWrapper.js';
import ArrowWrapper from './ArrowWrapper.js';

const MenuWrapperDiv = styled(Div)`
  display: flex;
  align-items: center;
  user-select: none;
  height: 100%;
  flex-grow: 1;
  -webkit-box-align: center;
  box-align: center;
  overflow: hidden;
`;

const MenuContentDiv = styled(Div)`
  flex: 1 1 0;
  overflow: hidden;
  user-select: none;
  height: 100%;
  position: relative;
`;

const ArrowContentDiv = styled(Div)`
  --width: 30px;
  --margin: -30px;
  --transform: 300px;

  position: relative;
  min-height: ${({
  theme
}) => theme.shape.headerBottomHeight};
  height: 100%;
  width: var(--width);

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    height: 200%;
    width: 200%;
    border-radius: 50%;
    box-shadow: -300px 0 20px 1px rgba(255, 255, 255, 0.9);
  }

  ${({
  left
}) => {
  if (left) {
    return css`
        --gap: calc(var(--width) * 1.3);
        margin-right: var(--margin);
        &::after {
          transform: translate(calc(var(--transform) - var(--gap)), -50%);
        }
      `;
  }
  return css`
      margin-left: var(--margin);
      &::after {
        transform: translate(var(--transform), -50%);
      }
    `;
}};
`;

const Arrow = props => /*#__PURE__*/createElement(ArrowContentDiv, props);
class ScrollingMenu extends Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "menuWrapperRef", /*#__PURE__*/createRef());
    _defineProperty(this, "state", {
      leftArrowVisible: false,
      rightArrowVisible: true,
      dragging: false,
      firstItemVisible: true,
      lastItemVisible: false,
      startDragTranslate: 0,
      translate: this.props.translate,
      xDraggedDistance: 0,
      xPoint: 0,
      minTranslate: 0,
      maxTranslate: 0
    });
    /** set ref for MenuItems */
    _defineProperty(this, "setRef", ref => {
      const [key, value] = Object.entries(ref)[0];
      if (value.elem) {
        this.ref[key] = value;
      }
    });
    _defineProperty(this, "setMenuInnerRef", ref => {
      this.menuInner = ref;
    });
    /** check first and last items and setState */
    _defineProperty(this, "setFirstLastItemVisibility", () => {
      const {
        firstItemVisible: firstItemVisibleOld,
        lastItemVisible: lastItemVisibleOld
      } = this.state;
      const {
        firstItemVisible,
        lastItemVisible
      } = this.checkFirstLastItemVisibility({});
      if (firstItemVisibleOld !== firstItemVisible || lastItemVisibleOld !== lastItemVisible) {
        const leftArrowVisible = !firstItemVisible;
        const rightArrowVisible = !lastItemVisible;
        this.setState(prevState => _objectSpread2(_objectSpread2({}, prevState), {}, {
          firstItemVisible,
          lastItemVisible,
          leftArrowVisible,
          rightArrowVisible
        }));
      }
    });
    _defineProperty(this, "onLoad", () => {
      this.setInitial();
      this.mounted = true;
    });
    /** set initial values and for updates */
    _defineProperty(this, "setInitial", () => {
      const {
        selected,
        data,
        translate: translateProp,
        scrollToSelected
      } = this.props;
      const {
        translate: translateState
      } = this.state;
      if (!data || !data.length) {
        return;
      }
      if (!this.menuItems.length || data !== this.data) {
        this.menuItems = this.getMenuItems();
        this.data = data;
      }

      // align item on initial load
      this.updateWidth({});
      const newState = _objectSpread2({}, this.state);

      // set translate on first load
      const firstMountAndDefaultTranslate = !this.mounted && translateProp === defaultProps.translate;
      if (firstMountAndDefaultTranslate || !translateIsValid(translateProp) && !translateIsValid(translateState)) {
        // newState.translate = alignCenter
        //   ? this.firstPageOffset
        //   : defaultProps.translate;
        newState.translate = 0;
      }

      // check arrows
      const {
        firstItemVisible,
        lastItemVisible
      } = this.checkFirstLastItemVisibility({
        translate: translateProp
      });
      newState.firstItemVisible = firstItemVisible;
      newState.lastItemVisible = lastItemVisible;
      newState.leftArrowVisible = !firstItemVisible;
      newState.rightArrowVisible = !lastItemVisible;

      // scrollToSelected
      if (scrollToSelected) {
        const needScroll = this.isScrollNeeded({
          itemId: selected,
          translate: newState.translate
        });
        if (needScroll) {
          newState.translate = this.getOffsetToItemByKey(selected);
        }
      }
      this.setState(_objectSpread2({}, newState));
    });
    /** get MenuItems from refs */
    _defineProperty(this, "getMenuItems", () => Object.entries(this.ref).slice(0, this.props.data.length || 0));
    /** get width of all menu items */
    _defineProperty(this, "getItemsWidth", ({
      items = this.menuItems
    }) => {
      return items.map(el => el[1].elem).filter(Boolean).reduce((acc, el) => acc + getClientRect(el).width, 0);
    });
    _defineProperty(this, "checkIfAllItemsVisible", () => {
      const visibleItems = this.getVisibleItems({});
      const allItems = this.getMenuItems();
      return visibleItems.length === allItems.length;
    });
    /** check if first and last items visible */
    _defineProperty(this, "checkFirstLastItemVisibility", ({
      translate = this.state.translate
    }) => {
      let firstItemVisible = true;
      let lastItemVisible = false;
      if (this.menuItems) {
        const visibleItems = this.getVisibleItems({
          offset: translate
        });
        firstItemVisible = visibleItems.includes(this.menuItems[0]);
        lastItemVisible = visibleItems.includes(this.menuItems.slice(-1)[0]);
      }
      return {
        firstItemVisible,
        lastItemVisible
      };
    });
    /** kinda debounce */
    _defineProperty(this, "resizeHandler", () => {
      const {
        alignOnResize
      } = this.props;
      if (!alignOnResize) {
        return;
      }
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.resize(), 250);
    });
    /** Set values on resize */
    _defineProperty(this, "resize", () => {
      const {
        alignCenter
      } = this.props;
      this.updateWidth({});
      const visibleItems = this.getVisibleItems({});
      const offset = this.getOffsetToItemByIndex({
        index: visibleItems[0] && visibleItems[0][1].index || 0
      });
      const align = alignCenter ? this.getCenterOffset({
        items: visibleItems
      }) : 0;
      this.setState(prevState => _objectSpread2(_objectSpread2({}, prevState), {}, {
        translate: -offset + align
      }));
    });
    /** check if selected item visible on screen or need scroll to it */
    _defineProperty(this, "isScrollNeeded", ({
      itemId,
      translate = this.state.translate
    }) => {
      const item = this.getItemByKey(itemId);
      const visibleItems = this.getVisibleItems({
        offset: translate
      });
      return !visibleItems.includes(item);
    });
    /** external api, scroll to item by it key */
    _defineProperty(this, "scrollTo", itemKey => {
      const {
        translate
      } = this.state;
      const newTranslate = this.getOffsetToItemByKey(itemKey);
      this.selected = itemKey;
      if (translate === newTranslate) {
        return;
      }
      this.setState({
        translate: newTranslate
      });
    });
    /** get width of items, window and pos of menu */
    _defineProperty(this, "getWidth", ({
      items
    }) => {
      const wWidth = window && window.innerWidth;
      const {
        left: menuPos,
        width: menuWidth
      } = this.getMenuWrapperBoundingClientRect();
      const allItemsWidth = this.getItemsWidth({
        items
      });
      return {
        wWidth,
        menuPos,
        menuWidth,
        allItemsWidth
      };
    });
    _defineProperty(this, "updateWidth", ({
      items = this.menuItems
    }) => {
      const {
        wWidth,
        menuPos,
        menuWidth,
        allItemsWidth
      } = this.getWidth({
        items
      });
      const {
        firstPageOffset,
        lastPageOffset
      } = this.getPagesOffsets({
        allItemsWidth,
        items,
        menuPos,
        menuWidth,
        wWidth
      });
      this.menuPos = menuPos;
      this.wWidth = wWidth;
      this.allItemsWidth = allItemsWidth;
      this.menuWidth = menuWidth;
      this.firstPageOffset = firstPageOffset;
      this.lastPageOffset = lastPageOffset;
    });
    _defineProperty(this, "getFirstPageOffset", ({
      items = this.menuItems,
      offset = this.state.translate,
      menuWidth = this.menuWidth
    }) => {
      const visibleItemsStart = this.getVisibleItems({
        items,
        menuWidth,
        offset
      });
      const firstPageOffset = this.getCenterOffset({
        menuWidth,
        items: visibleItemsStart
      });
      return firstPageOffset;
    });
    _defineProperty(this, "getLastPageOffset", ({
      items = this.menuItems,
      allItemsWidth = this.allItemsWidth,
      menuWidth = this.menuWidth
    }) => {
      const visibleItemsEnd = this.getVisibleItems({
        items,
        menuWidth,
        offset: -allItemsWidth + menuWidth
      });
      const lastPageOffset = this.getCenterOffset({
        menuWidth,
        items: visibleItemsEnd
      });
      return lastPageOffset;
    });
    _defineProperty(this, "getPagesOffsets", ({
      items = this.menuItems,
      allItemsWidth = this.allItemsWidth,
      wWidth = this.wWidth,
      menuPos = this.menuPos,
      menuWidth = this.menuWidth,
      offset = this.state.translate
    }) => {
      const firstPageOffset = this.getFirstPageOffset({
        items,
        menuPos,
        menuWidth,
        offset,
        wWidth
      });
      const lastPageOffset = this.getLastPageOffset({
        allItemsWidth,
        items,
        menuPos,
        menuWidth,
        wWidth
      });
      return {
        firstPageOffset,
        lastPageOffset
      };
    });
    _defineProperty(this, "onItemClick", id => {
      const {
        clickWhenDrag,
        onSelect
      } = this.props;
      const {
        xDraggedDistance
      } = this.state;
      const afterScroll = xDraggedDistance > 5;
      if (afterScroll && !clickWhenDrag) {
        return;
      }
      this.selected = id;
      if (onSelect) {
        onSelect(id);
      }
    });
    _defineProperty(this, "getVisibleItems", ({
      items = this.menuItems,
      menuWidth = this.menuWidth,
      offset = this.state.translate,
      translate = this.state.translate || defaultProps.translate
    }) => {
      return items.filter(el => {
        const {
          width: elWidth
        } = getClientRect(el[1].elem);
        const id = this.getItemInd(items, el);
        const x = this.getOffsetToItemByIndex({
          translate,
          index: id,
          menuItems: items
        });
        return this.elemVisible({
          elWidth,
          menuWidth,
          offset,
          x
        });
      });
    });
    _defineProperty(this, "elemVisible", ({
      x,
      offset = 0,
      elWidth,
      menuWidth = this.menuWidth
    }) => {
      const leftEdge = -1;
      const rightEdge = menuWidth + 1;
      const pos = x + offset;
      const posWithWidth = pos + elWidth;
      return pos >= leftEdge && posWithWidth <= rightEdge;
    });
    _defineProperty(this, "getItemInd", (menuItems = this.menuItems, item) => {
      if (!menuItems || !item) {
        return 0;
      }
      return menuItems.findIndex(el => el[0] === item[0]);
    });
    _defineProperty(this, "getNextItemInd", (left, visibleItems) => {
      if (left) {
        if (!visibleItems.length) {
          return 0;
        }
      } else if (!visibleItems.length) {
        return this.menuItems.length;
      }
      const ind = left ? this.getItemInd(this.menuItems, visibleItems[0]) - 1 : this.getItemInd(this.menuItems, visibleItems.slice(-1)[0]) + 1;
      return ind < 0 ? 0 : ind;
    });
    _defineProperty(this, "getOffsetToItemByKey", key => {
      let {
        translate
      } = this.state;
      const itemIndex = this.getItemIndexByKey(key);
      if (itemIndex === -1) {
        return translate;
      }
      const {
        alignCenter
      } = this.props;
      translate = this.getOffsetToItemByIndex({
        index: itemIndex
      });
      const visibleItemsWithNewTranslate = this.getVisibleItems({
        offset: -translate
      });
      const offset = alignCenter ? this.getCenterOffset({
        items: visibleItemsWithNewTranslate
      }) : defaultProps.translate;
      translate = -(translate - offset);
      if (this.itBeforeStart(translate)) {
        translate = this.getOffsetAtStart();
      } else if (this.itAfterEnd(translate)) {
        translate = this.getOffsetAtEnd();
      }
      return translate;
    });
    _defineProperty(this, "getItemByKey", key => {
      return this.menuItems.find(el => el[1].key === key) || ['', {
        key: 'n',
        elem: null,
        index: -1
      }];
    });
    _defineProperty(this, "getItemIndexByKey", itemKey => {
      if (!itemKey) {
        return -1;
      }
      return this.menuItems.findIndex(el => el[1].key === itemKey);
    });
    _defineProperty(this, "getOffsetToItemByIndex", ({
      index,
      menuItems = this.menuItems,
      translate = this.state.translate
    }) => {
      if (!menuItems.length) {
        return 0;
      }
      const ind = index >= menuItems.length ? menuItems.length - 1 : index;
      const {
        x
      } = getClientRect(menuItems[ind][1].elem);
      const position = +x - translate - this.menuPos;
      return position;
    });
    _defineProperty(this, "getScrollRightOffset", (visibleItems, items = this.menuItems) => {
      const {
        scrollBy
      } = this.props;
      const nextItemIndex = scrollBy ? visibleItems[0][1].index + scrollBy : this.getNextItem((visibleItems.slice(-1)[0] && visibleItems.slice(-1) || items.slice(-1))[0][1].key)[1].index;
      const newOffset = -this.getOffsetToItemByIndex({
        index: nextItemIndex,
        menuItems: items
      });
      return newOffset;
    });
    _defineProperty(this, "getScrollLeftOffset", (visibleItems, items = this.menuItems) => {
      const {
        scrollBy
      } = this.props;
      const prevItem = this.getPrevItem((visibleItems[0] && visibleItems[0][1] || items[0][1]).key);
      const prevItemIndex = prevItem[1].index - (scrollBy ? scrollBy - 1 : visibleItems.length);
      const newOffset = -this.getOffsetToItemByIndex({
        index: prevItemIndex < 0 ? 0 : prevItemIndex,
        menuItems: items
      });
      return newOffset;
    });
    _defineProperty(this, "getNextItem", key => {
      const itemIndex = this.menuItems.findIndex(el => el[1].key === key);
      const nextItemIndex = itemIndex + 1;
      const nextItem = this.menuItems[nextItemIndex] || this.menuItems.slice(-1)[0];
      return nextItem;
    });
    _defineProperty(this, "getPrevItem", key => {
      const itemIndex = this.menuItems.findIndex(el => el[1].key === key);
      const prevItemIndex = itemIndex - 1;
      const prevItem = this.menuItems[prevItemIndex] || this.menuItems[0];
      return prevItem;
    });
    _defineProperty(this, "getOffset", (left, items = this.menuItems) => {
      const visibleItems = this.getVisibleItems({
        items
      });
      const newOffset = left ? this.getScrollLeftOffset(visibleItems, items) : this.getScrollRightOffset(visibleItems, items);
      return newOffset;
    });
    _defineProperty(this, "getCenterOffset", ({
      items = this.menuItems,
      menuWidth = this.menuWidth
    }) => {
      if (!items.length) {
        return 0;
      }
      const itemsWidth = this.getItemsWidth({
        items
      });
      const offset = (menuWidth - itemsWidth) / 2;
      return offset;
    });
    _defineProperty(this, "handleWheel", e => {
      const {
        wheel
      } = this.props;
      if (!wheel) {
        return;
      }
      if (e.deltaY < 0) {
        this.handleArrowClick();
      } else {
        this.handleArrowClick(false);
      }
    });
    _defineProperty(this, "getOffsetAtStart", () => {
      const {
        alignCenter
      } = this.props;
      return alignCenter ? this.firstPageOffset : defaultProps.translate;
    });
    _defineProperty(this, "getOffsetAtEnd", () => {
      const {
        alignCenter
      } = this.props;
      const offset = this.allItemsWidth - this.menuWidth;
      return alignCenter ? -offset - this.lastPageOffset : -offset;
    });
    _defineProperty(this, "handleArrowClickRight", () => {
      this.handleArrowClick(false);
    });
    _defineProperty(this, "handleArrowClick", (left = true) => {
      const {
        alignCenter
      } = this.props;
      if (!alignCenter && !left && this.allItemsWidth < this.menuWidth) {
        return;
      }
      let newTranslate = 0;
      const visibleItems = this.getVisibleItems({});
      const firstItemVisible = visibleItems[0] && visibleItems[0][1].index === 0;
      const lastItemVisible = visibleItems.slice(-1)[0] && visibleItems.slice(-1)[0][1].index === this.menuItems.length - 1;
      const transl = this.getOffset(left);
      if (left && (firstItemVisible || this.itBeforeStart(transl))) {
        newTranslate = this.getOffsetAtStart();
      } else if (!left && (lastItemVisible || this.itAfterEnd(transl))) {
        newTranslate = this.getOffsetAtEnd();
      } else {
        // eslint-disable-next-line no-shadow
        const visibleItems = () => this.getVisibleItems({
          offset: transl
        });
        const centerOffset = alignCenter ? this.getCenterOffset({
          items: visibleItems()
        }) : 0;
        newTranslate = transl + centerOffset;
      }
      this.setState({
        startDragTranslate: 0,
        translate: newTranslate,
        xDraggedDistance: 0,
        xPoint: defaultProps.xPoint
      });
    });
    _defineProperty(this, "itBeforeStart", trans => {
      const {
        alignCenter
      } = this.props;
      if (this.allItemsWidth < this.menuWidth) {
        return true;
      }
      return alignCenter ? trans > this.firstPageOffset : trans > defaultProps.translate;
    });
    _defineProperty(this, "itAfterEnd", trans => {
      const {
        alignCenter
      } = this.props;
      if (this.allItemsWidth < this.menuWidth) {
        return true;
      }
      return alignCenter ? trans < defaultProps.translate && Math.abs(trans) > this.allItemsWidth - this.menuWidth + this.lastPageOffset : trans < defaultProps.translate && Math.abs(trans) > this.allItemsWidth - this.menuWidth;
    });
    _defineProperty(this, "getPoint", ev => {
      if ('touches' in ev) {
        return ev.touches[0].clientX;
      }
      if ('clientX' in ev) {
        return ev.clientX;
      }
      return 0;
    });
    _defineProperty(this, "handleDragStart", ev => {
      const optionsCapture = this.supportPassiveEvents ? {
        passive: true,
        capture: true
      } : true;
      window.addEventListener('mousemove', this.handleDrag, optionsCapture);
      window.addEventListener('mouseup', this.handleDragStop, optionsCapture);

      // 1 left button, 2 right button
      if (ev && 'buttons' in ev && ev.buttons === 2) {
        return;
      }
      const {
        dragging: draggingEnable
      } = this.props;
      if (!draggingEnable) {
        return;
      }
      const {
        translate: startDragTranslate
      } = this.state;

      // record drag events
      this.dragHistory = [{
        time: Date.now(),
        position: startDragTranslate
      }];
      this.setState({
        startDragTranslate,
        dragging: true,
        xDraggedDistance: 0,
        xPoint: 0
      });
    });
    _defineProperty(this, "handleDrag", e => {
      // Disable dragging if all items are visible
      if (this.checkIfAllItemsVisible()) {
        return;
      }
      const {
        dragging: draggingEnable
      } = this.props;
      const {
        translate,
        dragging,
        xPoint,
        xDraggedDistance
      } = this.state;
      if (!draggingEnable || !dragging) {
        return;
      }
      const point = this.getPoint(e);
      const diff = xPoint === defaultProps.xPoint ? defaultProps.xPoint : xPoint - point;
      let result = translate - diff;

      // don't let scroll over start and end
      if (this.itBeforeStart(result)) {
        result = this.getOffsetAtStart();
      } else if (this.itAfterEnd(result)) {
        result = this.getOffsetAtEnd();
      }
      if (diff !== 0) {
        this.dragHistory.push({
          time: Date.now(),
          position: result
        });
      }
      const newTranslate = result;
      this.setState(prevState => _objectSpread2(_objectSpread2({}, prevState), {}, {
        translate: newTranslate,
        xDraggedDistance: xDraggedDistance + Math.abs(diff),
        xPoint: point
      }));
    });
    _defineProperty(this, "handleDragStop", e => {
      window.removeEventListener('mousemove', this.handleDrag);
      window.removeEventListener('mouseup', this.handleDragStop);
      let {
        translate,
        xPoint = this.getPoint(e)
      } = this.state;
      const {
        dragging,
        startDragTranslate
      } = this.state;
      const {
        dragging: draggingEnable,
        alignCenter
      } = this.props;
      if (!draggingEnable || !dragging) {
        return;
      }

      // calculate inertia
      if (this.props.inertiaScrolling) {
        const currentTime = Date.now();
        const recentEntries = this.dragHistory.filter(entry => currentTime - entry.time < 150);
        if (recentEntries.length > 2) {
          const first = recentEntries[0];
          const last = recentEntries[recentEntries.length - 1];
          let speed = (last.position - first.position) / (last.time - first.time);
          speed *= this.props.inertiaScrollingSlowdown;
          translate += speed * (this.props.transition * 1000);
        }
      }
      let newTranslate = translate;
      if (this.itBeforeStart(translate)) {
        newTranslate = this.getOffsetAtStart();
        xPoint = defaultProps.xPoint;
      } else if (this.itAfterEnd(translate)) {
        newTranslate = this.getOffsetAtEnd();
        xPoint = defaultProps.xPoint;
      }
      if (!alignCenter && this.allItemsWidth <= this.menuWidth) {
        newTranslate = defaultProps.translate;
        xPoint = defaultProps.xPoint;
      }
      this.setState(prevState => _objectSpread2(_objectSpread2({}, prevState), {}, {
        xPoint,
        dragging: false,
        translate: newTranslate
      }), () => this.onUpdate({
        translate: newTranslate,
        translateOld: startDragTranslate
      }));
    });
    _defineProperty(this, "isArrowsVisible", () => {
      const {
        hideArrows
      } = this.props;
      const hide = Boolean(hideArrows && this.allItemsWidth <= this.menuWidth);
      return !hide;
    });
    _defineProperty(this, "onUpdate", ({
      translate = this.state.translate,
      translateOld = this.state.translate
    }) => {
      const {
        onUpdate
      } = this.props;
      if (translate !== translateOld && translate !== this.lastTranslateUpdate) {
        this.lastTranslateUpdate = translate;
        if (typeof onUpdate === 'function') {
          onUpdate({
            translate
          });
        }
      }
    });
    this.ref = {};
    this.menuInner = null;
    this.mounted = false;
    this.needUpdate = false;
    this.allItemsWidth = 0;
    this.menuPos = 0;
    this.menuWidth = 0;
    this.wWidth = 0;
    this.firstPageOffset = 0;
    this.lastPageOffset = 0;
    this.lastTranslateUpdate = 0;
    this.menuItems = [];
    this.selected = String(props.selected) || '';
    this.onLoadTimer = 0;
    this.rafTimer = 0;
    this.resizeTimer = 0;
    this.data = null;
    this.dragHistory = [];
  }
  componentDidMount() {
    this.setInitial();

    // @ts-ignore
    window.requestAnimationFrame = window.requestAnimationFrame || (() => false);
    this.supportPassiveEvents = testPassiveEventSupport();
    const optionsNoCapture = this.supportPassiveEvents ? {
      passive: true,
      capture: false
    } : false;
    window.addEventListener('load', this.onLoad, optionsNoCapture);
    window.addEventListener('resize', this.resizeHandler, optionsNoCapture);

    // if styles loaded before js bundle need wait for it
    // eslint-disable-next-line no-sequences
    this.onLoadTimer = setTimeout(() => (this.onLoad(), this.forceUpdate()), 0);
  }
  shouldComponentUpdate(nextProps, nextState) {
    const {
      translate,
      dragging,
      firstItemVisible,
      lastItemVisible
    } = this.state;
    const {
      translate: translateNew,
      dragging: draggingNew,
      firstItemVisible: firstItemVisibleNew,
      lastItemVisible: lastItemVisibleNew
    } = nextState;
    const {
      translate: translateProps,
      selected: selectedProps,
      scrollToSelected
    } = this.props;
    const {
      translate: translatePropsNew,
      selected: selectedPropsNew
    } = nextProps;
    const translatePropsNotNull = notUndefOrNull(translatePropsNew);
    const translateStateDiff = translate !== translateNew;
    const translatePropsDiff = translatePropsNotNull && translateProps !== translatePropsNew;
    const translateDiff = translatePropsNew !== translateNew || translateStateDiff || translatePropsDiff;
    const selectedPropsDiff = notUndefOrNull(selectedPropsNew) && selectedProps !== selectedPropsNew;
    const selectedDiff = selectedPropsDiff || this.selected !== selectedPropsNew;
    const propsDiff = translateDiff || selectedDiff;
    const firstItemVisibleDiff = firstItemVisible !== firstItemVisibleNew;
    const lastItemVisibleDiff = lastItemVisible !== lastItemVisibleNew;
    let translateResult = translateNew;
    const newMenuItems = this.props.data !== nextProps.data || this.props.data.length !== nextProps.data.length;
    const newTranslateProps = translateIsValid(translatePropsNew) && translatePropsDiff && !newMenuItems;
    if (newMenuItems || scrollToSelected && selectedPropsDiff) {
      this.needUpdate = true;
    }
    if (propsDiff) {
      if (selectedPropsDiff) {
        this.selected = selectedPropsNew;
      }
      if (newTranslateProps) {
        translateResult = translatePropsNew;
      }
    }
    if (newTranslateProps) {
      this.setState(prevState => _objectSpread2(_objectSpread2({}, prevState), {}, {
        translate: +translateResult
      }));
    }
    return newMenuItems || translateDiff || dragging !== draggingNew || propsDiff || firstItemVisibleDiff || lastItemVisibleDiff;
  }
  componentDidUpdate(_, prevState) {
    // update if have new menu items or selected value
    if (this.needUpdate) {
      this.needUpdate = false;
      this.onLoad();
    }
    const {
      translate: translateOld
    } = prevState;
    const {
      translate,
      dragging
    } = this.state;
    if (!dragging && translateOld !== translate) {
      this.onUpdate({
        translate,
        translateOld
      });
    }
    const {
      hideSingleArrow,
      transition
    } = this.props;
    if (hideSingleArrow) {
      requestAnimationFrame(this.setFirstLastItemVisibility);
      this.rafTimer = setTimeout(() => requestAnimationFrame(this.setFirstLastItemVisibility), transition * 1000 + 10);
    }
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeHandler);
    window.removeEventListener('mousemove', this.handleDrag);
    window.removeEventListener('mouseup', this.handleDragStop);
    clearTimeout(this.rafTimer);
    clearTimeout(this.onLoadTimer);
    clearTimeout(this.resizeTimer);
  }
  getMenuWrapperBoundingClientRect() {
    if (this.menuWrapperRef.current) {
      return this.menuWrapperRef.current.getBoundingClientRect();
    }
    return {
      left: 0,
      width: 0
    };
  }
  render() {
    const {
      arrowClass,
      arrowDisabledClass,
      arrowLeft,
      arrowRight,
      data,
      inertiaScrolling,
      innerWrapperStyle,
      menuStyle,
      menuWrapperStyle,
      innerWrapperClass,
      itemStyle,
      itemClass,
      itemClassActive,
      transition,
      useButtonRole
    } = this.props;
    const {
      translate,
      dragging,
      leftArrowVisible,
      rightArrowVisible
    } = this.state;
    if (!data || !data.length) {
      return null;
    }
    const arrowsVisible = this.mounted ? this.isArrowsVisible() : true;
    const itemWrapperStyle = _objectSpread2(_objectSpread2(_objectSpread2({}, defaultProps.itemStyle), itemStyle), {
      height: '100%'
    });
    const arrowProps = {
      className: arrowClass,
      disabledClass: arrowDisabledClass
    };
    return /*#__PURE__*/createElement(MenuWrapperDiv, {
      style: menuWrapperStyle
    }, arrowLeft && /*#__PURE__*/createElement(ArrowWrapper, _extends({}, arrowProps, {
      isDisabled: !arrowsVisible || !leftArrowVisible,
      onClick: this.handleArrowClick
    }), arrowLeft), /*#__PURE__*/createElement(MenuContentDiv, {
      elRef: this.menuWrapperRef,
      onMouseDown: this.handleDragStart,
      onTouchStart: this.handleDragStart,
      onTouchEnd: this.handleDragStop,
      onMouseMove: this.handleDrag,
      onTouchMove: this.handleDrag,
      style: menuStyle
    }, /*#__PURE__*/createElement(InnerWrapper, {
      data: data,
      translate: translate,
      dragging: dragging,
      mounted: this.mounted,
      transition: this.mounted ? transition : 0,
      selected: this.selected,
      setRef: this.setRef,
      setMenuInnerRef: this.setMenuInnerRef,
      onClick: this.onItemClick,
      innerWrapperStyle: innerWrapperStyle,
      innerWrapperClass: innerWrapperClass,
      itemStyle: itemWrapperStyle,
      itemClass: itemClass,
      itemClassActive: itemClassActive,
      inertiaScrolling: inertiaScrolling,
      useButtonRole: useButtonRole
    })), arrowRight && /*#__PURE__*/createElement(ArrowWrapper, _extends({}, arrowProps, {
      isDisabled: !arrowsVisible || !rightArrowVisible,
      onClick: this.handleArrowClickRight
    }), arrowRight));
  }
}
_defineProperty(ScrollingMenu, "ShadowAsArrow", Arrow);
_defineProperty(ScrollingMenu, "defaultProps", defaultProps);

export default ScrollingMenu;
